// Audience map for the ticketing widget — modeled on teliance-chat's "Chat
// Origins" card. Leaflet map with graduated circle markers per city plus a
// top-cities bar chart. Data = /universe-widget/geo-summary: per-city counts
// for EVERY funnel stage (sessions carry CF geo ~100%), so the stage chips
// switch metric client-side with no refetch; the days window refetches
// upstream (handled by the parent).
//
// Plain Leaflet (no react-leaflet): the map is initialized imperatively into a
// ref once, and markers are re-drawn in a layer group when data/metric change.
import { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

const STAGES = [
  { key: 'views',     label: 'Views' },
  { key: 'opens',     label: 'Opens' },
  { key: 'cart',      label: 'Cart' },
  { key: 'info',      label: 'Info' },
  { key: 'payment',   label: 'Payment' },
  { key: 'purchased', label: 'Purchased' },
];

// Graduated tiers like the chat map: biggest cities red, mid yellow, rest blue.
function tierColor(value, max) {
  if (max > 0 && value >= max * 0.5) return '#ef4444';
  if (max > 0 && value >= max * 0.15) return '#eab308';
  return '#3b82f6';
}

// stage/onStageChange are owned by the parent: this component unmounts while
// the Analytics tab reloads a new time window, and must not forget the
// selected stage across that remount.
export default function AudienceMap({ data, loading, stage = 'opens', onStageChange }) {
  const setStage = onStageChange || (() => {});
  const mapRef = useRef(null);       // Leaflet map instance
  const layerRef = useRef(null);     // marker layer group
  const boxRef = useRef(null);       // container div

  const cities = useMemo(
    () => (data?.cities || []).filter((c) => c.lat != null && c.lon != null && (c[stage] || 0) > 0),
    [data, stage],
  );
  const maxVal = cities.length ? Math.max(...cities.map((c) => c[stage] || 0)) : 0;

  // Init the map once.
  useEffect(() => {
    if (!boxRef.current || mapRef.current) return;
    // preferCanvas: the full uncapped city list can be several thousand
    // markers; canvas draws them as pixels instead of one SVG node each.
    const map = L.map(boxRef.current, { scrollWheelZoom: false, preferCanvas: true }).setView([25.79, -80.13], 3);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; layerRef.current = null; };
  }, []);

  // Redraw markers when data or stage changes.
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    if (!cities.length) return;
    const maxR = Math.sqrt(maxVal || 1);
    for (const c of cities) {
      const v = c[stage] || 0;
      const radius = Math.max(4, Math.min(30, (Math.sqrt(v) / maxR) * 30));
      L.circleMarker([c.lat, c.lon], {
        radius,
        color: tierColor(v, maxVal),
        fillColor: tierColor(v, maxVal),
        fillOpacity: 0.6,
        weight: 1,
      }).bindTooltip(
        `${c.city}${c.region ? `, ${c.region}` : ''} — ${v.toLocaleString()} ${STAGES.find((s) => s.key === stage)?.label.toLowerCase()}`
          + (stage !== 'purchased' && c.purchased ? ` · ${c.purchased.toLocaleString()} purchased` : ''),
      ).addTo(layer);
    }
    // Fit to the top cities (95% of volume) so one London dot doesn't zoom the
    // world out; cap at ~40 markers for the bounds calc.
    const top = [...cities].sort((a, b) => (b[stage] || 0) - (a[stage] || 0)).slice(0, 40);
    map.fitBounds(L.latLngBounds(top.map((c) => [c.lat, c.lon])).pad(0.2), { maxZoom: 10 });
  }, [cities, stage, maxVal]);

  const bars = useMemo(
    () => [...cities]
      .sort((a, b) => (b[stage] || 0) - (a[stage] || 0))
      .slice(0, 15)
      .map((c) => ({ name: `${c.city}${c.region ? `, ${c.region}` : ''}`, value: c[stage] || 0 })),
    [cities, stage],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-1 text-xs">
        {STAGES.map((s) => (
          <button
            key={s.key}
            onClick={() => setStage(s.key)}
            className={`px-2.5 py-1 rounded-md border ${stage === s.key ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-input text-muted-foreground hover:bg-accent'}`}
          >{s.label}</button>
        ))}
        <span className="ml-auto text-muted-foreground">
          {loading ? 'Loading…' : data ? (
            stage === 'views'
              ? `${(data.totals?.views_located ?? 0).toLocaleString()} views located · ${cities.length.toLocaleString()} cities`
              : `${(data.totals?.with_geo ?? 0).toLocaleString()} sessions located · ${cities.length.toLocaleString()} cities`
          ) : ''}
        </span>
      </div>

      <div ref={boxRef} className="rounded-md overflow-hidden border border-border" style={{ height: 400 }} />

      <div>
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">
          Top cities, {STAGES.find((s) => s.key === stage)?.label.toLowerCase()}
        </h3>
        {bars.length ? (
          <div style={{ height: Math.max(160, bars.length * 26 + 40) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bars} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="text-border" stroke="currentColor" />
                <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground py-4">No located sessions in this window.</div>
        )}
      </div>
    </div>
  );
}